import {Helmet} from '@dr.pogodin/react-helmet';
import {FormSubmitHandler} from 'platform/components';

import {useParams} from 'react-router-dom';

import {defaultTo, isNotNil} from 'ramda';

import {
  PostSpaceResponse,
  useGetSpaceQuery,
  usePatchSpaceMutation,
  usePostSpaceMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {handleApiError, usePermissions} from '@dms/shared';

import {ApiError, composePath, useNavigate} from 'shared/web';

import {GeneralTab} from './components/GeneralTab';
import {StorageLocations} from './components/StorageLocations';
import {WarehouseSpaceForm} from './types/WarehouseSpaceForm';

export function WarehouseSpacesDetail() {
  const params = useParams();
  const id = params.id;
  const navigate = useNavigate();
  const isEditing = isNotNil(id);

  const [warehouseDictionaryEdit, warehouseDictionaryCreate] = usePermissions({
    permissionKeys: ['warehouseDictionaryEdit', 'warehouseDictionaryCreate'],
  });

  const spaceId = defaultTo('', id);

  const spaceQuery = useGetSpaceQuery({spaceId}, {skip: !isEditing});

  const [postSpace, postStatus] = usePostSpaceMutation();
  const [patchSpace, patchStatus] = usePatchSpaceMutation();

  const isEditDisabled = isEditing ? !warehouseDictionaryEdit : !warehouseDictionaryCreate;
  const isMutating = postStatus.isLoading || patchStatus.isLoading;

  const defaultValues: WarehouseSpaceForm = {
    isActive: spaceQuery.data?.isActive ?? true,
    name: spaceQuery.data?.name ?? '',
    assignWarehouses: spaceQuery.data?.assignWarehouses ?? [],
  };

  const handleSubmit: FormSubmitHandler<WarehouseSpaceForm> = async (
    data,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    const mutation = isEditing
      ? patchSpace({
          spaceId,
          body: {
            isActive: data.isActive,
            name: data.name,
            assignWarehouses: data.assignWarehouses,
          },
        })
      : postSpace({
          isActive: data.isActive,
          name: data.name,
          assignWarehouses: data.assignWarehouses,
        });

    await mutation
      .unwrap()
      .then((response) => {
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        if (isEditing) {
          navigate(settingsRoutes.warehousesSpaces);
        } else {
          navigate(
            composePath(settingsRoutes.warehousesSpacesDetail, {
              params: {id: (response as PostSpaceResponse).spaceId},
              queryParams: {tab: 'locations'},
            })
          );
        }
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const header: SettingsTemplateHeader = {
    breadcrumbs: [
      {
        label: i18n.t('entity.warehouse.labels.spaces'),
        href: settingsRoutes.warehousesSpaces,
      },
    ],
    title: isEditing
      ? defaultTo('', spaceQuery.data?.name)
      : i18n.t('entity.warehouse.labels.newSpace'),
    alerts: isEditDisabled
      ? [
          {
            type: 'banner',
            variant: 'info',
            message: isEditing
              ? i18n.t('entity.warehouse.labels.missingWarehouseDictionaryEditPermission')
              : i18n.t('entity.warehouse.labels.missingWarehouseDictionaryCreatePermission'),
          },
        ]
      : undefined,
  };

  const tabs = [
    {
      queryId: 'general',
      title: i18n.t('general.labels.general'),
      content: (
        <GeneralTab
          onSubmit={handleSubmit}
          defaultValues={defaultValues}
          isLoading={isMutating}
          isEditing={isEditing}
          isDisabled={isEditDisabled}
        />
      ),
    },
    {
      queryId: 'locations',
      title: i18n.t('entity.warehouse.labels.locations'),
      isDisabled: !isEditing,
      content: (
        <StorageLocations
          spaceId={spaceId}
          spaceName={spaceQuery.data?.name}
          warehouseId={spaceQuery.data?.assignWarehouses?.[0]}
          isDisabled={isEditDisabled || !isEditing}
        />
      ),
    },
  ];

  return (
    <>
      <Helmet title={i18n.t('entity.warehouse.labels.spaceDetail')} />
      <SettingsTemplate
        header={header}
        tabs={tabs}
        isLoading={spaceQuery.isLoading}
        isError={spaceQuery.isError}
        data-testid={testIds.settings.warehousesSpacesDetail('template')}
      />
    </>
  );
}
